import {
  Badge, Button, FileInput, Group, Modal, Paper, Progress, Select, Stack, Table, Text, TextInput, Title,
} from "@mantine/core";
import { DateInput } from "./_date";
import { notifications } from "@mantine/notifications";
import { IconPlus, IconUpload } from "@tabler/icons-react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api, getAuthHeader, unwrap } from "../api/client";
import { useCan } from "../auth/user";
import { useT } from "../i18n/script";

const STATUS_COLOR: Record<string, string> = { ready: "green", valid: "green", invalid: "red", optimizing: "blue",
  validating: "blue", uploaded: "gray", draft: "gray", processing: "blue" };
const KINDS = [
  { value: "ortho_ms", label: "Multispektralni ortofoto (GeoTIFF)" }, { value: "ortho_rgb", label: "RGB ortofoto" },
  { value: "dsm", label: "DSM (GeoTIFF)" }, { value: "dtm", label: "DTM (GeoTIFF)" },
  { value: "pointcloud", label: "Oblak tačaka (LAS/LAZ)" },
];
const KIND_SHORT: Record<string, string> = { ortho_ms: "Multispektralni ortofoto", ortho_rgb: "RGB ortofoto", dsm: "DSM",
  dtm: "DTM", ndsm: "nDSM", pointcloud: "Oblak tačaka" };
const STATUS_LABEL: Record<string, string> = { ready: "spreman", valid: "validan", invalid: "neispravan",
  optimizing: "optimizacija", validating: "validacija", uploaded: "učitan", draft: "nacrt", processing: "obrada" };
const META_LABELS: [string, string][] = [["crs", "Koordinatni sistem"], ["width", "Širina (px)"], ["height", "Visina (px)"],
  ["count", "Broj kanala"], ["dtype", "Tip podataka"], ["gsd_cm", "Rezolucija (cm/px)"], ["max_height_m", "Najveća visina (m)"],
  ["point_count", "Broj tačaka"]];
type AssetDetail = { id: number; kind: string; original_name: string; status: string; size_bytes: number | null;
  validation: { errors?: string[]; warnings?: string[] } | null; metadata: Record<string, unknown> | null };

export default function UavPage() {
  const { t } = useT();
  const can = useCan();
  const qc = useQueryClient();
  const sites = useQuery({ queryKey: ["sites"], queryFn: async () => unwrap(await api.GET("/api/v1/uav/sites")) as
    { id: number; code: string; name: string; survey_count: number; last_survey: string | null }[] });
  const surveys = useQuery({ queryKey: ["surveys"], refetchInterval: 5000,
    queryFn: async () => unwrap(await api.GET("/api/v1/uav/surveys")) as
      { id: number; site_code: string; site_name: string; survey_date: string; sensor: string | null; gsd_cm: number | null;
        status: string; assets: { id: number; kind: string; status: string }[] }[] });
  const [siteForm, setSiteForm] = useState<{ code: string; name: string } | null>(null);
  const [surveyForm, setSurveyForm] = useState<{ site_id: string | null; survey_date: string | null; sensor: string } | null>(null);
  const [upload, setUpload] = useState<{ survey_id: number; kind: string; file: File | null } | null>(null);
  const [progress, setProgress] = useState<number | null>(null);
  const [detail, setDetail] = useState<{ survey: number; asset: number } | null>(null);
  const assetInfo = useQuery({ queryKey: ["survey-detail", detail?.survey], enabled: !!detail,
    queryFn: async () => unwrap(await api.GET("/api/v1/uav/surveys/{survey_id}", { params: { path: { survey_id: detail!.survey } } })) as
      { assets: AssetDetail[] } });
  const asset = assetInfo.data?.assets.find((a) => a.id === detail?.asset);

  const fail = (e: unknown) => notifications.show({ color: "red", message: String(e) });

  async function createSite() {
    try { unwrap(await api.POST("/api/v1/uav/sites", { body: { ...siteForm!, aoi: null } })); setSiteForm(null);
      qc.invalidateQueries({ queryKey: ["sites"] }); } catch (e) { fail(e); }
  }
  async function createSurvey() {
    try {
      unwrap(await api.POST("/api/v1/uav/surveys", { body: { site_id: Number(surveyForm!.site_id),
        survey_date: surveyForm!.survey_date!, sensor: surveyForm!.sensor || null, weather: {} } }));
      setSurveyForm(null); qc.invalidateQueries({ queryKey: ["surveys"] });
    } catch (e) { fail(e); }
  }
  /** Upload sa napretkom (XHR) — fajlovi mogu biti vise GB. */
  async function doUpload() {
    if (!upload?.file) return;
    const fd = new FormData();
    fd.append("kind", upload.kind);
    fd.append("file", upload.file);
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/uav/surveys/${upload.survey_id}/assets`);
    const h = await getAuthHeader();
    if (h) xhr.setRequestHeader("Authorization", h);
    xhr.upload.onprogress = (e) => e.lengthComputable && setProgress((e.loaded / e.total) * 100);
    xhr.onload = () => {
      setProgress(null);
      if (xhr.status < 300) {
        notifications.show({ color: "green", message: t("Fajl je učitan — validacija i optimizacija (COG/COPC) u toku. Klik na oznaku proizvoda prikazuje rezultat.") });
        setUpload(null); qc.invalidateQueries({ queryKey: ["surveys"] });
      } else fail(xhr.responseText);
    };
    xhr.onerror = () => { setProgress(null); fail(t("Učitavanje nije uspjelo")); };
    xhr.send(fd);
  }

  return (
    <Stack>
      <Group justify="space-between">
        <div>
          <Title order={3}>{t("UAV snimanja")}</Title>
          <Text size="sm" c="dimmed">{t("Registar lokaliteta i setova; uvoz ortofoto, DSM/DTM i oblaka tačaka (automatska validacija, COG i COPC)")}</Text>
        </div>
        {can("uav:upload") && (
          <Group>
            <Button variant="default" leftSection={<IconPlus size={16} />} onClick={() => setSiteForm({ code: "", name: "" })}>{t("Lokalitet")}</Button>
            <Button leftSection={<IconPlus size={16} />} onClick={() => setSurveyForm({ site_id: null, survey_date: null, sensor: "" })}>{t("Set snimanja")}</Button>
          </Group>
        )}
      </Group>
      <Paper withBorder>
        <Table fz="sm" striped>
          <Table.Thead><Table.Tr><Table.Th>{t("Lokalitet")}</Table.Th><Table.Th>{t("Datum")}</Table.Th><Table.Th>{t("Senzor")}</Table.Th>
            <Table.Th>GSD (cm)</Table.Th><Table.Th>{t("Proizvodi")}</Table.Th><Table.Th>Status</Table.Th><Table.Th /></Table.Tr></Table.Thead>
          <Table.Tbody>
            {(surveys.data ?? []).map((s) => (
              <Table.Tr key={s.id}>
                <Table.Td><b>{s.site_code}</b> {s.site_name}</Table.Td>
                <Table.Td>{s.survey_date}</Table.Td><Table.Td>{s.sensor ?? "—"}</Table.Td><Table.Td>{s.gsd_cm ?? "—"}</Table.Td>
                <Table.Td><Group gap={4}>{s.assets.map((a) => (
                  <Badge key={a.id} size="sm" variant="light" color={STATUS_COLOR[a.status]} style={{ cursor: "pointer" }}
                    title={t("Detalji i rezultat validacije")} onClick={() => setDetail({ survey: s.id, asset: a.id })}>
                    {t(KIND_SHORT[a.kind] ?? a.kind)} · {t(STATUS_LABEL[a.status] ?? a.status)}</Badge>))}</Group></Table.Td>
                <Table.Td><Badge color={STATUS_COLOR[s.status]}>{t(STATUS_LABEL[s.status] ?? s.status)}</Badge></Table.Td>
                <Table.Td>{can("uav:upload") && <Button size="xs" variant="light" leftSection={<IconUpload size={14} />}
                  onClick={() => setUpload({ survey_id: s.id, kind: "ortho_ms", file: null })}>{t("Učitaj proizvod")}</Button>}</Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
        {!surveys.data?.length && <Text c="dimmed" ta="center" py="lg" size="sm">{t("Još nema setova snimanja")}</Text>}
      </Paper>

      <Modal opened={!!siteForm} onClose={() => setSiteForm(null)} title={t("Novi lokalitet")}>
        {siteForm && <Stack>
          <TextInput label={t("Šifra (prefiks ID-a stabala)")} placeholder="LOK01" value={siteForm.code}
            onChange={(e) => setSiteForm({ ...siteForm, code: e.currentTarget.value.toUpperCase() })} />
          <TextInput label={t("Naziv")} value={siteForm.name} onChange={(e) => setSiteForm({ ...siteForm, name: e.currentTarget.value })} />
          <Button onClick={createSite}>{t("Sačuvaj")}</Button></Stack>}
      </Modal>
      <Modal opened={!!surveyForm} onClose={() => setSurveyForm(null)} title={t("Novi set snimanja")}>
        {surveyForm && <Stack>
          <Select label={t("Lokalitet")} data={(sites.data ?? []).map((s) => ({ value: String(s.id), label: `${s.code} — ${s.name}` }))}
            value={surveyForm.site_id} onChange={(v) => setSurveyForm({ ...surveyForm, site_id: v })} />
          <DateInput label={t("Datum snimanja")} value={surveyForm.survey_date} onChange={(v) => setSurveyForm({ ...surveyForm, survey_date: v })} />
          <TextInput label={t("Senzor")} placeholder="MicaSense RedEdge-P / DJI Mavic 3M" value={surveyForm.sensor}
            onChange={(e) => setSurveyForm({ ...surveyForm, sensor: e.currentTarget.value })} />
          <Button onClick={createSurvey} disabled={!surveyForm.site_id || !surveyForm.survey_date}>{t("Sačuvaj")}</Button></Stack>}
      </Modal>
      <Modal opened={!!upload} onClose={() => setUpload(null)} title={t("Učitavanje proizvoda")}>
        {upload && <Stack>
          <Select label={t("Vrsta proizvoda")} data={KINDS.map((k) => ({ ...k, label: t(k.label) }))} value={upload.kind}
            onChange={(v) => setUpload({ ...upload, kind: v ?? "ortho_ms" })} />
          <FileInput label={t("Fajl")} accept=".tif,.tiff,.las,.laz" value={upload.file} onChange={(f) => setUpload({ ...upload, file: f })} />
          {progress != null && <Progress value={progress} animated />}
          <Button onClick={doUpload} disabled={!upload.file} loading={progress != null}>{t("Pošalji")}</Button></Stack>}
      </Modal>
      <Modal opened={!!detail} onClose={() => setDetail(null)} title={t("Proizvod snimanja")} size="lg">
        {asset ? <Stack gap="xs">
          <Group gap="xs"><Text fw={600}>{t(KIND_SHORT[asset.kind] ?? asset.kind)}</Text>
            <Badge color={STATUS_COLOR[asset.status]}>{t(STATUS_LABEL[asset.status] ?? asset.status)}</Badge></Group>
          <Text size="sm">{t("Fajl")}: {asset.original_name}{asset.size_bytes ? ` (${(asset.size_bytes / 1048576).toFixed(1)} MB)` : ""}</Text>
          {(asset.validation?.errors ?? []).length > 0 && <Paper withBorder p="sm" bg="red.0">
            <Text fw={600} c="red.8" size="sm">{t("Greške — proizvod je odbijen")}</Text>
            {asset.validation!.errors!.map((e, i) => <Text key={i} size="sm">• {t(e)}</Text>)}</Paper>}
          {(asset.validation?.warnings ?? []).length > 0 && <Paper withBorder p="sm" bg="yellow.0">
            <Text fw={600} c="yellow.9" size="sm">{t("Upozorenja")}</Text>
            {asset.validation!.warnings!.map((e, i) => <Text key={i} size="sm">• {t(e)}</Text>)}</Paper>}
          {asset.metadata && <Table fz="xs" withTableBorder>
            <Table.Tbody>{META_LABELS.filter(([k]) => asset.metadata![k] != null).map(([k, label]) => (
                <Table.Tr key={k}><Table.Td>{t(label)}</Table.Td><Table.Td>{String(asset.metadata![k])}</Table.Td></Table.Tr>))}</Table.Tbody>
          </Table>}
        </Stack> : <Text size="sm" c="dimmed">{t("Učitavanje…")}</Text>}
      </Modal>
    </Stack>
  );
}
